/** Stateless keyboard policy shared by the campaign shell and the local practice room. */
export const FT3D_KEY_DEFAULTS: Readonly<Record<string, string>> = Object.freeze({
    main_hand: 'f',
    off_hand: 'g',
    camera_forward: 'w',
    camera_back: 's',
    camera_left: 'a',
    camera_right: 'd',
    camera_spin_left: 'q',
    camera_spin_right: 'e',
    action_bar: 'b',
    inventory: 'i',
    skills: 'k',
    character: 'n',
    hiding: 'c',
    party: 'p',
    ping: 'r',
    cancel_settings: 'escape',
    gm_visibility: 'shift+l',
    room_outlines: 'o',
    director_spotlight: 'l',
    unit_next: 'tab',
    unit_prev: 'shift+tab',
    dice: 'v',
    map: 'm',
    // Last, so an existing custom binding on T keeps it and the ruler starts unbound for that player.
    ruler: 't',
});
export function normalizeFt3dBinding(value: unknown): string {
    var tokens = String(value || '').toLowerCase().split('+').map(function(token) {
        return token.trim();
    }).filter(Boolean);
    if (!tokens.length) return '';
    var key = tokens[tokens.length - 1]!;
    var modifiers = tokens.slice(0, -1);
    if (!/^(?:[a-z0-9]|escape|enter|tab|space|arrowup|arrowdown|arrowleft|arrowright|pageup|pagedown|home|end|insert|f(?:[1-9]|1[0-2])|[,.\/;'\[\]`\\=-])$/.test(key)) return '';
    if (modifiers.some(function(modifier) {
        return ['ctrl', 'alt', 'shift'].indexOf(modifier) < 0;
    })) return '';
    var unique: string[] = [];
    ['ctrl', 'alt', 'shift'].forEach(function(modifier) {
        if (modifiers.indexOf(modifier) >= 0) unique.push(modifier);
    });
    return unique.concat(key).join('+');
}

export function ft3dBindingFromEvent(event: KeyboardEvent): string {
    if (event.metaKey) return '';
    var raw = String(event.key || '').toLowerCase();
    if (['control', 'alt', 'shift', 'meta', 'altgraph'].indexOf(raw) >= 0) return '';
    var aliases: Record<string, string> = {
        ' ': 'space', spacebar: 'space', esc: 'escape',
        'arrowup': 'arrowup', 'arrowdown': 'arrowdown',
        'arrowleft': 'arrowleft', 'arrowright': 'arrowright',
    };
    var key = aliases[raw] || raw;
    var prefix = [];
    if (event.ctrlKey) prefix.push('ctrl');
    if (event.altKey) prefix.push('alt');
    if (event.shiftKey) prefix.push('shift');
    return normalizeFt3dBinding(prefix.concat(key).join('+'));
}

export function formatFt3dBinding(value: unknown): string {
    var binding = normalizeFt3dBinding(value);
    if (!binding) return 'Unbound';
    var names: Record<string, string> = {
        ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', escape: 'Esc', enter: 'Enter', tab: 'Tab',
        space: 'Space', arrowup: 'Up', arrowdown: 'Down', arrowleft: 'Left', arrowright: 'Right',
        pageup: 'Page Up', pagedown: 'Page Down', home: 'Home', end: 'End', insert: 'Insert',
    };
    return binding.split('+').map(function(token) {
        return names[token] || (/^[a-z]$/.test(token) ? token.toUpperCase() : token);
    }).join('+');
}

export function readFt3dKeyBindings(storage: Pick<Storage, 'getItem'>, scope: string): Record<string, string> {
    const ft3dKeyBindings: Record<string, string> = {};
    const ft3dKeyStorageName = `ft3d:key-bindings:${scope}:v1`;
    var stored: Record<string, unknown> | null = null;
    try { stored = JSON.parse(storage.getItem(ft3dKeyStorageName) || 'null'); }
    catch (error) { stored = null; }
    // The original v1 map used C for Character sheet and had no Hiding action. Move that exact
    // old default to N so returning players receive the new Crouch/Character convention without
    // disturbing any other customized binding in their account-scoped browser preference.
    if (stored && typeof stored === 'object'
        && !Object.prototype.hasOwnProperty.call(stored, 'hiding')
        && normalizeFt3dBinding(stored.character) === 'c'
        && !Object.keys(stored).some(function(action) {
            return action !== 'character' && normalizeFt3dBinding(stored?.[action]) === 'n';
        })) {
        stored.character = 'n';
    }
    var used: Record<string, string> = Object.create(null);
    Object.keys(FT3D_KEY_DEFAULTS).forEach(function(action) {
        var candidate = stored && Object.prototype.hasOwnProperty.call(stored, action)
            ? normalizeFt3dBinding(stored[action])
            : FT3D_KEY_DEFAULTS[action]!;
        if (candidate && used[candidate]) candidate = '';
        ft3dKeyBindings[action] = candidate;
        if (candidate) used[candidate] = action;
    });
    return ft3dKeyBindings;
}

/** Uses the event's own document, including a same-origin scene frame. */
export function ft3dShortcutTyping(event: KeyboardEvent): boolean {
    const target = event.target as Element | null;
    return !!target?.matches && (target.matches('input, textarea, select') || !!target.closest('[contenteditable="true"]'));
}

export function fireFt3dHandAction(action: string, dock: HTMLElement | null): boolean {
    if (!['main_hand', 'off_hand'].includes(action) || !dock || dock.hidden) return false;
    const button = dock.querySelector<HTMLButtonElement>(`.ft-action-slot[data-ft-action-binding="${action}"] button`);
    if (!button || button.disabled) return false;
    const slot = button.closest('.ft-action-slot');
    slot?.classList.add('ft-action-slot--keyfire');
    setTimeout(() => slot?.classList.remove('ft-action-slot--keyfire'), 160);
    button.setAttribute('data-ft-action-keyfire', '1');
    try { button.click(); } finally { button.removeAttribute('data-ft-action-keyfire'); }
    return true;
}

export interface ActorShortcutHost {
    active(): boolean;
    actor(): string;
    dock(): HTMLElement | null;
    matches(event: KeyboardEvent, action: string): boolean;
    toggleDock(force?: boolean): void;
    toggleInventory(): void;
    toggleSheet(): void;
}

/** Phases keep the campaign's Escape chain and Party ordering exactly where they were. */
export function handleActorShortcut(event: KeyboardEvent, phase: 'hand' | 'dock' | 'character', host: ActorShortcutHost): boolean {
    if (event.repeat || ft3dShortcutTyping(event)) return false;
    const consume = (): true => { event.preventDefault(); event.stopImmediatePropagation(); return true; };
    const dock = host.dock();
    if (phase === 'hand') {
        const hand = host.matches(event, 'main_hand') ? 'main_hand' : host.matches(event, 'off_hand') ? 'off_hand' : '';
        return !!(hand && host.active() && fireFt3dHandAction(hand, dock) && consume());
    }
    if (!host.active()) return false;
    // The dock toggle owns its named no-actor refusal even before a character is selected.
    if (phase === 'dock' && host.matches(event, 'action_bar')) { consume(); host.toggleDock(); return true; }
    if (!host.actor()) return false;
    const page = window as Window & {
        ftActionBarCycleUnit?: (direction: number) => boolean;
        ftActionBarToggleSkills?: (actor: string) => void;
    };
    if (phase === 'character') {
        if (!window.matchMedia('(min-width: 1025px)').matches || !host.matches(event, 'character')) return false;
        consume(); host.toggleSheet(); return true;
    }
    const focus = document.activeElement;
    const free = !focus || focus === document.body || focus.tagName === 'IFRAME' || !!focus.closest('.ft-action-units');
    if (free && (host.matches(event, 'unit_next') || host.matches(event, 'unit_prev'))
        && page.ftActionBarCycleUnit?.(host.matches(event, 'unit_prev') ? -1 : 1)) return consume();
    if (host.matches(event, 'inventory')) { consume(); host.toggleInventory(); return true; }
    if (host.matches(event, 'skills')) {
        consume(); if (dock?.hidden) host.toggleDock(true);
        page.ftActionBarToggleSkills?.(host.actor()); return true;
    }
    if (host.matches(event, 'hiding')) {
        consume();
        const button = dock?.querySelector<HTMLButtonElement>('[data-ft3d-hiding-toggle]');
        if (button && !button.disabled) button.click();
        return true;
    }
    return false;
}

export const ft3dActorShortcuts = {
    defaults: FT3D_KEY_DEFAULTS, normalize: normalizeFt3dBinding, fromEvent: ft3dBindingFromEvent,
    format: formatFt3dBinding, read: readFt3dKeyBindings, typing: ft3dShortcutTyping,
    fireHand: fireFt3dHandAction, handle: handleActorShortcut,
};

